function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
function _classCallCheck(a, n) { if (!(a instanceof n)) throw new TypeError("Cannot call a class as a function"); }
function _defineProperties(e, r) { for (var t = 0; t < r.length; t++) { var o = r[t]; o.enumerable = o.enumerable || !1, o.configurable = !0, "value" in o && (o.writable = !0), Object.defineProperty(e, _toPropertyKey(o.key), o); } }
function _createClass(e, r, t) { return r && _defineProperties(e.prototype, r), t && _defineProperties(e, t), Object.defineProperty(e, "prototype", { writable: !1 }), e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == _typeof(i) ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != _typeof(t) || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != _typeof(i)) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _callSuper(t, o, e) { return o = _getPrototypeOf(o), _possibleConstructorReturn(t, _isNativeReflectConstruct() ? Reflect.construct(o, e || [], _getPrototypeOf(t).constructor) : o.apply(t, e)); }
function _possibleConstructorReturn(t, e) { if (e && ("object" == _typeof(e) || "function" == typeof e)) return e; if (void 0 !== e) throw new TypeError("Derived constructors may only return object or undefined"); return _assertThisInitialized(t); }
function _assertThisInitialized(e) { if (void 0 === e) throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); return e; }
function _isNativeReflectConstruct() { try { var t = !Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); } catch (t) {} return (_isNativeReflectConstruct = function _isNativeReflectConstruct() { return !!t; })(); }
function _getPrototypeOf(t) { return _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf.bind() : function (t) { return t.__proto__ || Object.getPrototypeOf(t); }, _getPrototypeOf(t); }
function _inherits(t, e) { if ("function" != typeof e && null !== e) throw new TypeError("Super expression must either be null or a function"); t.prototype = Object.create(e && e.prototype, { constructor: { value: t, writable: !0, configurable: !0 } }), Object.defineProperty(t, "prototype", { writable: !1 }), e && _setPrototypeOf(t, e); }
function _setPrototypeOf(t, e) { return _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function (t, e) { return t.__proto__ = e, t; }, _setPrototypeOf(t, e); }
/**
 * @module ol/source/DataTile
 */
import DataTile from '../DataTile.js';
import TileState from '../TileState.js';
import EventType from '../events/EventType.js';
import { toPromise } from '../functions.js';
import { equivalent, get as getProjection } from '../proj.js';
import ReprojDataTile from '../reproj/DataTile.js';
import { toSize } from '../size.js';
import { createXYZ, extentFromProjection, getForProjection as _getTileGridForProjection } from '../tilegrid.js';
import { getUid } from '../util.js';
import TileSource, { TileSourceEvent } from './Tile.js';
import TileEventType from './TileEventType.js';

/**
 * @typedef {'anonymous'|'use-credentials'} CrossOriginAttribute
 */

/**
 * @typedef {Object} LoaderOptions
 * @property {AbortSignal} signal An abort controller signal.
 * @property {CrossOriginAttribute} [crossOrigin] The cross-origin attribute for images.
 * @property {number} [maxY] The maximum y coordinate at the given z level.  Will be undefined if the
 * underlying tile grid does not have a known extent.
 */

/**
 * Data tile loading function.  The function is called with z, x, and y tile coordinates and
 * returns {@link import("../DataTile.js").Data data} for a tile or a promise for the same.
 * @typedef {function(number, number, number, LoaderOptions) : (import("../DataTile.js").Data|Promise<import("../DataTile.js").Data>)} Loader
 */

/**
 * @typedef {Object} Options
 * @property {Loader} [loader] Data loader.  Called with z, x, and y tile coordinates.
 * Returns {@link import("../DataTile.js").Data data} for a tile or a promise for the same.
 * For loaders that generate images, the promise should not resolve until the image is loaded.
 * @property {import("./Source.js").AttributionLike} [attributions] Attributions.
 * @property {boolean} [attributionsCollapsible=true] Attributions are collapsible.
 * @property {number} [maxZoom=42] Optional max zoom level. Not used if `tileGrid` is provided.
 * @property {number} [minZoom=0] Optional min zoom level. Not used if `tileGrid` is provided.
 * @property {number|import("../size.js").Size} [tileSize=[256, 256]] The pixel width and height of the source tiles.
 * This may be different than the rendered pixel size if a `tileGrid` is provided.
 * @property {number} [gutter=0] The size in pixels of the gutter around data tiles to ignore.
 * This allows artifacts of rendering at tile edges to be ignored.
 * Supported data should be wider and taller than the tile size by a value of `2 x gutter`.
 * @property {number} [maxResolution] Optional tile grid resolution at level zero. Not used if `tileGrid` is provided.
 * @property {import("../proj.js").ProjectionLike} [projection='EPSG:3857'] Tile projection.
 * @property {import("../tilegrid/TileGrid.js").default} [tileGrid] Tile grid.
 * @property {import("./Source.js").State} [state] The source state.
 * @property {boolean} [wrapX=false] Render tiles beyond the antimeridian.
 * @property {number} [transition] Transition time when fading in new tiles (in milliseconds).
 * @property {number} [bandCount=4] Number of bands represented in the data.
 * @property {boolean} [interpolate=false] Use interpolated values when resampling.  By default,
 * the nearest neighbor is used when resampling.
 * @property {CrossOriginAttribute} [crossOrigin='anonymous'] The crossOrigin property to pass to loaders for image data.
 * @property {string} [key] Key for use in caching tiles.
 * @property {number|import("../array.js").NearestDirectionFunction} [zDirection=0]
 * Choose whether to use tiles with a higher or lower zoom level when between integer
 * zoom levels. See {@link module:ol/tilegrid/TileGrid~TileGrid#getZForResolution}.
 */

/**
 * @classdesc
 * A source for typed array data tiles.
 *
 * @fires import("./Tile.js").TileSourceEvent
 * @template {import("../Tile.js").default} [TileType=DataTile]
 * @extends TileSource<TileType>
 * @api
 */
var DataTileSource = /*#__PURE__*/function (_TileSource) {
  /**
   * @param {Options} options DataTile source options.
   */
  function DataTileSource(options) {
    var _this;
    _classCallCheck(this, DataTileSource);
    var projection = options.projection === undefined ? 'EPSG:3857' : options.projection;
    var tileGrid = options.tileGrid;
    if (tileGrid === undefined && projection) {
      tileGrid = createXYZ({
        extent: extentFromProjection(projection),
        maxResolution: options.maxResolution,
        maxZoom: options.maxZoom,
        minZoom: options.minZoom,
        tileSize: options.tileSize
      });
    }
    _this = _callSuper(this, DataTileSource, [{
      cacheSize: 0.1,
      // don't cache on the source
      attributions: options.attributions,
      attributionsCollapsible: options.attributionsCollapsible,
      projection: projection,
      tileGrid: tileGrid,
      state: options.state,
      wrapX: options.wrapX,
      transition: options.transition,
      interpolate: options.interpolate,
      key: options.key,
      zDirection: options.zDirection
    }]);

    /**
     * @private
     * @type {number}
     */
    _this.gutter_ = options.gutter !== undefined ? options.gutter : 0;

    /**
     * @private
     * @type {import('../size.js').Size|null}
     */
    _this.tileSize_ = options.tileSize ? toSize(options.tileSize) : null;

    /**
     * @private
     * @type {Array<import('../size.js').Size>|null}
     */
    _this.tileSizes_ = null;

    /**
     * @private
     * @type {!Object<string, boolean>}
     */
    _this.tileLoadingKeys_ = {};

    /**
     * @private
     */
    _this.loader_ = options.loader;

    /**
     * @private
     */
    _this.handleTileChange_ = _this.handleTileChange_.bind(_this);

    /**
     * @type {number}
     */
    _this.bandCount = options.bandCount === undefined ? 4 : options.bandCount; // assume RGBA if undefined

    /**
     * @private
     * @type {!Object<string, import("../tilegrid/TileGrid.js").default>}
     */
    _this.tileGridForProjection_ = {};

    /**
     * @private
     * @type {CrossOriginAttribute}
     */
    _this.crossOrigin_ = options.crossOrigin || 'anonymous';

    /**
     * @type {import("../transform.js").Transform|null}
     */
    _this.transformMatrix = null;
    return _this;
  }

  /**
   * Set the source tile sizes.  The length of the array is expected to match the number of
   * levels in the tile grid.
   * @protected
   * @param {Array<import('../size.js').Size>} tileSizes An array of tile sizes.
   */
  _inherits(DataTileSource, _TileSource);
  return _createClass(DataTileSource, [{
    key: "setTileSizes",
    value: function setTileSizes(tileSizes) {
      this.tileSizes_ = tileSizes;
    }

    /**
     * Get the source tile size at the given zoom level.  This may be different than the rendered tile
     * size.
     * @protected
     * @param {number} z Tile zoom level.
     * @return {import('../size.js').Size} The source tile size.
     */
  }, {
    key: "getTileSize",
    value: function getTileSize(z) {
      if (this.tileSizes_) {
        return this.tileSizes_[z];
      }
      if (this.tileSize_) {
        return this.tileSize_;
      }
      var tileGrid = this.getTileGrid();
      return tileGrid ? toSize(tileGrid.getTileSize(z)) : [256, 256];
    }

    /**
     * @param {import("../proj/Projection.js").default} projection Projection.
     * @return {number} Gutter.
     * @override
     */
  }, {
    key: "getGutterForProjection",
    value: function getGutterForProjection(projection) {
      var thisProj = this.getProjection();
      if ((!thisProj || equivalent(thisProj, projection)) && !this.transformMatrix) {
        return this.gutter_;
      }
      return 0;
    }

    /**
     * @param {Loader} loader The data loader.
     * @protected
     */
  }, {
    key: "setLoader",
    value: function setLoader(loader) {
      this.loader_ = loader;
    }

    /**
     * @param {number} z Tile coordinate z.
     * @param {number} x Tile coordinate x.
     * @param {number} y Tile coordinate y.
     * @param {import("../proj/Projection.js").default} targetProj The output projection.
     * @param {import("../proj/Projection.js").default} sourceProj The input projection.
     * @return {!TileType} Tile.
     */
  }, {
    key: "getReprojTile_",
    value: function getReprojTile_(z, x, y, targetProj, sourceProj) {
      var _this2 = this;
      var sourceTileGrid = this.tileGrid || this.getTileGridForProjection(sourceProj || targetProj);
      var reprojTilePixelRatio = Math.max.apply(null, sourceTileGrid.getResolutions().map(function (r, z) {
        var tileSize = toSize(sourceTileGrid.getTileSize(z));
        var textureSize = _this2.getTileSize(z);
        return Math.max(textureSize[0] / tileSize[0], textureSize[1] / tileSize[1]);
      }));
      var targetTileGrid = this.getTileGridForProjection(targetProj);
      var tileCoord = [z, x, y];
      var wrappedTileCoord = this.getTileCoordForTileUrlFunction(tileCoord, targetProj);
      var options = Object.assign({
        sourceProj: sourceProj || targetProj,
        sourceTileGrid: sourceTileGrid,
        targetProj: targetProj,
        targetTileGrid: targetTileGrid,
        tileCoord: tileCoord,
        wrappedTileCoord: wrappedTileCoord,
        pixelRatio: reprojTilePixelRatio,
        gutter: this.gutter_,
        getTileFunction: function getTileFunction(z, x, y, pixelRatio) {
          return _this2.getTile(z, x, y, pixelRatio);
        },
        transformMatrix: this.transformMatrix
      }, /** @type {import("../reproj/DataTile.js").Options} */this.tileOptions);
      var tile = /** @type {TileType} */
      /** @type {*} */new ReprojDataTile(options);
      tile.key = this.getKey();
      return tile;
    }

    /**
     * @param {number} z Tile coordinate z.
     * @param {number} x Tile coordinate x.
     * @param {number} y Tile coordinate y.
     * @param {number} pixelRatio Pixel ratio.
     * @param {import("../proj/Projection.js").default} [projection] Projection.
     * @return {TileType|null} Tile (or null if outside source extent).
     * @override
     */
  }, {
    key: "getTile",
    value: function getTile(z, x, y, pixelRatio, projection) {
      var _this$getTileGrid;
      var sourceProjection = this.getProjection();
      if (projection && (sourceProjection && !equivalent(sourceProjection, projection) || this.transformMatrix)) {
        return this.getReprojTile_(z, x, y, projection, sourceProjection);
      }
      var size = this.getTileSize(z);
      var sourceLoader = this.loader_;
      var controller = new AbortController();

      /**
       * @type {LoaderOptions}
       */
      var loaderOptions = {
        signal: controller.signal,
        crossOrigin: this.crossOrigin_
      };
      var tileCoord = this.getTileCoordForTileUrlFunction([z, x, y]);
      if (!tileCoord) {
        return null;
      }
      var requestZ = tileCoord[0];
      var requestX = tileCoord[1];
      var requestY = tileCoord[2];
      var range = (_this$getTileGrid = this.getTileGrid()) === null || _this$getTileGrid === void 0 ? void 0 : _this$getTileGrid.getFullTileRange(requestZ);
      if (range) {
        loaderOptions.maxY = range.getHeight() - 1;
      }
      function loader() {
        return toPromise(function () {
          return sourceLoader(requestZ, requestX, requestY, loaderOptions);
        });
      }

      /**
       * @type {import("../DataTile.js").Options}
       */
      var options = Object.assign({
        tileCoord: [z, x, y],
        loader: loader,
        size: size,
        controller: controller
      }, this.tileOptions);
      var tile = /** @type {TileType} */
      /** @type {*} */new DataTile(options);
      tile.key = this.getKey();
      tile.addEventListener(EventType.CHANGE, this.handleTileChange_);
      return tile;
    }

    /**
     * Handle tile change events.
     * @param {import("../events/Event.js").default} event Event.
     */
  }, {
    key: "handleTileChange_",
    value: function handleTileChange_(event) {
      var tile = /** @type {import("../Tile.js").default} */event.target;
      var uid = getUid(tile);
      var tileState = tile.getState();
      var type;
      if (tileState == TileState.LOADING) {
        this.tileLoadingKeys_[uid] = true;
        type = TileEventType.TILELOADSTART;
      } else if (uid in this.tileLoadingKeys_) {
        delete this.tileLoadingKeys_[uid];
        type = tileState == TileState.ERROR ? TileEventType.TILELOADERROR : tileState == TileState.LOADED ? TileEventType.TILELOADEND : undefined;
      }
      if (type) {
        this.dispatchEvent(new TileSourceEvent(type, tile));
      }
    }

    /**
     * @param {import("../proj/Projection.js").default} projection Projection.
     * @return {!import("../tilegrid/TileGrid.js").default} Tile grid.
     * @override
     */
  }, {
    key: "getTileGridForProjection",
    value: function getTileGridForProjection(projection) {
      var thisProj = this.getProjection();
      if (this.tileGrid && (!thisProj || equivalent(thisProj, projection)) && !this.transformMatrix) {
        return this.tileGrid;
      }
      var projKey = getUid(projection);
      if (!(projKey in this.tileGridForProjection_)) {
        this.tileGridForProjection_[projKey] = _getTileGridForProjection(projection);
      }
      return this.tileGridForProjection_[projKey];
    }

    /**
     * Sets the tile grid to use when reprojecting the tiles to the given
     * projection instead of the default tile grid for the projection.
     *
     * This can be useful when the default tile grid cannot be created
     * (e.g. projection has no extent defined) or
     * for optimization reasons (custom tile size, resolutions, ...).
     *
     * @param {import("../proj.js").ProjectionLike} projection Projection.
     * @param {import("../tilegrid/TileGrid.js").default} tilegrid Tile grid to use for the projection.
     * @api
     */
  }, {
    key: "setTileGridForProjection",
    value: function setTileGridForProjection(projection, tilegrid) {
      var proj = getProjection(projection);
      if (proj) {
        var projKey = getUid(proj);
        if (!(projKey in this.tileGridForProjection_)) {
          this.tileGridForProjection_[projKey] = tilegrid;
        }
      }
    }
  }]);
}(TileSource);
export default DataTileSource;